import React from 'react';
import { X as XIcon, Sparkles, Star } from 'lucide-react';
import { PLAY_TEMPLATES } from '@/lib/playTemplates';
import { useLang } from '@/context/LangContext';

/**
 * Modal to pick a starter template. When applied, the editor replaces its
 * current name/category/difficulty/description/steps with the template.
 */
export default function PlayTemplatesModal({ open, onClose, onApply }) {
  const { t } = useLang();
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      data-testid="play-templates-modal"
      onClick={onClose}
    >
      <div
        className="bg-card-steel border-2 border-gold/50 rounded-lg w-full max-w-4xl p-5 sm:p-6 my-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">{t('tpl_modal_eyebrow')}</div>
            <h2 className="font-h uppercase text-2xl sm:text-3xl flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-gold" /> {t('tpl_modal_title')}
            </h2>
            <p className="text-xs text-gray-400 mt-1">
              {t('tpl_modal_subtitle')}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="play-templates-close"
            className="p-2 text-gray-400 hover:text-white"
            aria-label={t('close')}
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {PLAY_TEMPLATES.map((tpl) => {
            const diffKey = tpl.difficulty === 'beginner' ? 'diff_basic' : tpl.difficulty === 'intermediate' ? 'diff_medium' : 'diff_advanced';
            return (
              <button
                key={tpl.id}
                data-testid={`play-template-${tpl.id}`}
                onClick={() => onApply(tpl)}
                className="text-left bg-navy-deep border-2 border-steel hover:border-gold rounded-lg p-4 transition group"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-h uppercase text-lg text-white group-hover:text-gold transition">
                    {tpl.name}
                  </h3>
                  <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-gold/15 text-gold border border-gold/30">
                    {t(diffKey)}
                  </span>
                </div>
                <p className="text-xs text-gray-400 leading-relaxed mb-3 min-h-[60px]">
                  {tpl.description}
                </p>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-gray-500 font-mono">{tpl.steps.length} {t('tpl_steps_count')}</span>
                  <span className="inline-flex items-center gap-1 text-gold">
                    <Star className="w-3 h-3" /> {Math.round((tpl.success_rate || 0) * 100)}% {t('market_success')}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
